import { useEffect, useRef, useState } from "react";
import moment from "moment/moment";
import { CiFilter } from "react-icons/ci";
import { MdDeleteOutline, MdOutlineFileDownload } from "react-icons/md";
import { useNavigate } from "react-router-dom"; // Added useNavigate import
import Swal from "sweetalert2";
import { useMutation, useQuery } from "@tanstack/react-query";

import * as images from "../../assets/image";
import FilterModal from "../../common/FilterModal";
import PayoutModal from "../../common/PayoutModal";
import useRole from "../../hooks/useRole";
import { downloadTimesheet, getEventWithPayouts, softDeletePayout, updatePayout } from "../../services/services";
import { constant } from "../../utils/constants";
import { downloadFile } from "../../utils/function";
import Pagination from "../../utils/Pagination";
import { toastAlert } from "../../utils/SweetAlert";

const AdminPayout = () => {
  const [search, setSearch] = useState();
  const [page, setPage] = useState(constant.PAGE_NO_ONE);
  const [filterShow, setFilterShow] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [isToggled, setIsToggled] = useState(false);
  const tableContainerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate(); // Initialize useNavigate

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggle = () => {
    setPage(1);
    setIsToggled(!isToggled);
  };

  const role = useRole();

  const handleFilterShow = () => setFilterShow(true);

  // Reset page to 1 whenever filters or sort order change
  useEffect(() => {
    setPage(1);
  }, [filterData, isToggled]);

  const { data, refetch } = useQuery({
    queryKey: ["payout-list", page, filterData, isToggled],
    queryFn: async () => {
      let params = {
        page: page,
        sortedBy: "startTime",
      };

      if (isToggled) {
        params.previousEvents = true;
        params.sortBy = "DESC";
      } else {
        params.futureEvents = true;
        params.sortBy = "ASC";
      }

      if (
        Object.keys(filterData).length &&
        Object.keys(filterData).filter((item) => filterData[item]).length
      ) {
        params.range = `${filterData.start}TO${filterData.end}`;
        delete params.futureEvents;
        delete params.previousEvents;
      }
      if (search) params.search = search;
      const resp = await getEventWithPayouts(params);
      return resp?.data?.data || {};
    },
  });

  const handleSampleDownload = async () => {
    try {
      let params = {
        sortedBy: "name_date",
        sortBy: "ASC",
      };
      if (
        Object.keys(filterData).length &&
        Object.keys(filterData).filter((item) => filterData[item]).length
      ) {
        params.range = `${filterData.start}TO${filterData.end}`;
      }
      if (search) params.search = search;
      let resp = await downloadTimesheet(params);
      let fileName = params?.range
        ? `${params?.range}_timesheet.xlsx`
        : "timesheet.xlsx";
      downloadFile(resp?.data, fileName);
    } catch (err) {
      console.log("err", err);
    }
  };

  const handleEditClick = (payout = null, time) => {
    if (!payout) return null;
    setSelectedPayout({ ...payout, shiftDate: time });
    setShowEditModal(true);
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) => updatePayout(id, body),
    onSuccess: (resp) => {
      toastAlert("success", resp?.data?.message);
      refetch();
    },
  });

  const toggleSelection = (payoutId = null) => {
    if (!payoutId) return;
    setSelected((prev) =>
      prev.includes(payoutId)
        ? prev.filter((id) => id !== payoutId)
        : [...prev, payoutId]
    );
  };

  const toggleSelectAll = (event) => {
    const { target: { checked } } = event;
    if (!checked) return setSelected([]);
    const payoutIDs = [];
    data.events.forEach((payoutEvents) => {
      if (moment(payoutEvents?.startTime).isSameOrBefore())
        payoutIDs.push(payoutEvents._id);
    });
    setSelected(payoutIDs || []);
  };

  const handleSoftDelete = () => {
    if (!selected.length) {
      toastAlert("error", "No payout has been selected");
      return;
    }
    Swal.fire({
      title: "Are you sure?",
      text: `You are going to delete ${selected.length} events `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    }).then((result) => {
      if (result.isConfirmed) {
        if (selected.length) {
          let body = { eventIds: selected };
          softDeleteMutation.mutate(body);
        }
      }
    });
  };

  const softDeleteMutation = useMutation({
    mutationFn: (body) => softDeletePayout(body),
    onSuccess: (resp) => {
      toastAlert("success", resp?.data?.message);
      setSelected([]);
      refetch();
    },
  });

  // Handle navigation to add timesheet
  const handleTimesheet = (event) => {
    const { _id, clientDetails, startTime,employeeDetails, endTime } = event;
    const eventStartTime = moment(startTime);
    const eventEndTime = moment(endTime);
    const labelValue = `${eventStartTime.format("ll")} (${eventStartTime.format(
      constant.HOURS_FORMAT
    )} - ${eventEndTime.format(constant.HOURS_FORMAT)})`;
    const navigateData = {
      clientId: {
        label: `${clientDetails?.firstName} ${clientDetails?.lastName}`,
        value: clientDetails._id,
      },
      employeeId:{
         label: `${employeeDetails?.firstName} ${employeeDetails?.lastName}`,
        value: employeeDetails._id,
      },
      eventId: { label: labelValue, value: _id },
      checkIn: new Date(startTime),
      checkOut: new Date(endTime),
      date: new Date(startTime),
    };
    navigate("/admin/add-timesheet", {
      state: { formValues: navigateData },
    });
  };

  return (
    <section className="main-content">
      <div className="commonSearchBar mt-3">
        <h4 className="heading20">Payout</h4>
        <div className="endContent">
          {/* Desktop View - Original Layout */}
          <div className="d-none d-md-flex align-items-center">
            <div className="toggle-container me-2">
              <div
                className={`toggle-switch ${isToggled ? "toggled" : ""}`}
                onClick={handleToggle}
              >
                <div className={`slider ${isToggled ? "toggled" : ""}`}></div>
              </div>
              <label className="label-text">Past Assignments</label>
            </div>
            <div className="clientSearch me-2">
              <img
                src={images.Search}
                alt="searchImg"
                className="clientSearchIcon"
              />
              <input
                type="text"
                placeholder="Type and press enter to search."
                className="clientSearchInput"
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && search.trim() !== "" && refetch()
                }
                onKeyUp={(e) =>
                  e.target.value === "" && e.key === "Backspace" && refetch()
                }
              />
            </div>
            {role === constant.ROLE.ADMIN && (
              <>
                <div className="position-relative me-2">
                  {Object.keys(filterData).length &&
                  Object.keys(filterData).filter((item) => filterData[item])
                    .length ? (
                    <div className="redDot" />
                  ) : null}
                  <button
                    className="greenBtn calendar-btn"
                    type="button"
                    title="Filter By"
                    onClick={handleFilterShow}
                  >
                    <CiFilter size={35} />
                  </button>
                </div>
                <button
                  className="greenBtn calendar-btn me-2"
                  type="button"
                  title="Delete Selected"
                  onClick={handleSoftDelete}
                >
                  <MdDeleteOutline size={35} />
                </button>
                <button
                  className="greenBtn calendar-btn"
                  type="button"
                  title="Download"
                  onClick={handleSampleDownload}
                >
                  <MdOutlineFileDownload size={35} />
                </button>
              </>
            )}
          </div>

          {/* Mobile View - Responsive Layout */}
          <div className="d-flex d-md-none flex-column gap-2">
            <div className="d-flex justify-content-between align-items-center">
              <div className="toggle-container">
                <div
                  className={`toggle-switch ${isToggled ? "toggled" : ""}`}
                  onClick={handleToggle}
                >
                  <div className={`slider ${isToggled ? "toggled" : ""}`}></div>
                </div>
                <label className="label-text">Past Assignments</label>
              </div>
              {role === constant.ROLE.ADMIN && (
                <div className="d-flex gap-2">
                  <div className="position-relative">
                    {Object.keys(filterData).length &&
                    Object.keys(filterData).filter((item) => filterData[item])
                      .length ? (
                      <div className="redDot" />
                    ) : null}
                    <button
                      className="greenBtn calendar-btn p-1"
                      type="button"
                      title="Filter By"
                      onClick={handleFilterShow}
                    >
                      <CiFilter size={24} />
                    </button>
                  </div>
                  <button
                    className="greenBtn calendar-btn p-1"
                    type="button"
                    title="Delete Selected"
                    onClick={handleSoftDelete}
                  >
                    <MdDeleteOutline size={24} />
                  </button>
                  <button
                    className="greenBtn calendar-btn p-1"
                    type="button"
                    title="Download"
                    onClick={handleSampleDownload}
                  >
                    <MdOutlineFileDownload size={24} />
                  </button>
                </div>
              )}
            </div>
            <div className="clientSearch">
              <img
                src={images.Search}
                alt="searchImg"
                className="clientSearchIcon"
              />
              <input
                type="text"
                placeholder="Type and press enter to search."
                className="clientSearchInput"
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && search.trim() !== "" && refetch()
                }
                onKeyUp={(e) =>
                  e.target.value === "" && e.key === "Backspace" && refetch()
                }
              />
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid p-0">
        <div className="row mt-4">
          <div className="col-md-12">
            <div
              ref={tableContainerRef}
              style={{
                overflowX: isMobile ? "auto" : "visible",
                WebkitOverflowScrolling: "touch",
                width: "100%",
                position: "relative",
                paddingBottom: isMobile ? "20px" : "0",
              }}
            >
              {isMobile && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "#666",
                    fontSize: "12px",
                  }}
                >
                  ← Scroll →
                </div>
              )}

              <table
                className="responsive_table"
                style={{
                  width: "100%",
                  minWidth: isMobile ? "900px" : "100%",
                  borderCollapse: "separate",
                  borderSpacing: "0",
                }}
              >
                <thead style={{ backgroundColor: "#f8f9fa" }}>
                  <tr>
                    <th
                      className="tableHeading"
                      style={{
                        width: "40px",
                        minWidth: "40px",
                        left: isMobile ? "0" : "auto",
                        zIndex: isMobile ? "10" : "auto",
                        backgroundColor: isMobile ? "#f8f9fa" : "transparent",
                        borderRadius: "10px 0px 0px 0px",
                      }}
                    >
                      <input
                        type="checkbox"
                        onChange={toggleSelectAll}
                        checked={
                          data?.events?.length > 0 &&
                          selected.length === data?.events?.length
                        }
                      />
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        position: isMobile ? "sticky" : "static",
                        left: isMobile ? "0px" : "auto",
                        zIndex: isMobile ? "9" : "auto",
                        minWidth: isMobile ? "90px" : "120px",
                        backgroundColor: isMobile ? "#f8f9fa" : "transparent",
                      }}
                    >
                      Employee Name
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        position: isMobile ? "sticky" : "static",
                        left: isMobile ? "72px" : "auto",
                        zIndex: isMobile ? "8" : "auto",
                        minWidth: isMobile ? "80px" : "120px",
                        backgroundColor: isMobile ? "#f8f9fa" : "transparent",
                      }}
                    >
                      Client Name
                    </th>
                    <th
                      className="tableHeading"
                      style={{
                        left: "auto",
                        zIndex: "auto",
                        minWidth: "100px",
                        backgroundColor: "transparent",
                      }}
                    >
                      Assignment date
                    </th>
                    <th className="tableHeading">Check-In</th>
                    <th className="tableHeading">Check-Out</th>
                    <th className="tableHeading">Actual Check-In</th>
                    <th className="tableHeading">Actual Check-Out</th>
                    <th className="tableHeading">Client Initials</th>
                    <th className="tableHeading">Note</th>
                    <th
                      className="tableHeading"
                      style={{
                        minWidth: "90px",
                        borderRadius: "0px 10px 0px 0px",
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data && data?.events && data?.events?.length > 0 ? (
                    data?.events?.map((event, index) => {
                      const clientDetails = event?.clientDetails || null;
                      const employeeDetails = event?.employeeDetails || null;
                      const payoutDetails = event?.payoutDetails || null;
                      const hasPayout = payoutDetails?._id ? true : false;
                      const startTime = event?.startTime;
                      const isGreaterThanToday = new Date(startTime) > new Date();
                      console.log('isGreaterThanToday: ', event.startTime, isGreaterThanToday);
                      return (
                        <tr key={`event_${index}`}>
                          <td
                            style={{
                              width: "40px",
                              minWidth: "40px",
                              left: isMobile ? "0" : "auto",
                              zIndex: isMobile ? "10" : "auto",
                              backgroundColor: isMobile ? "white" : "transparent",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selected.includes(event._id)}
                              onChange={() => toggleSelection(event._id)}
                            />
                          </td>
                          <td
                            style={{
                              position: isMobile ? "sticky" : "static",
                              left: isMobile ? "0px" : "auto",
                              zIndex: isMobile ? "9" : "auto",
                              minWidth: isMobile ? "90px" : "120px",
                              backgroundColor: isMobile ? "white" : "transparent",
                            }}
                            className="text-capitalize"
                          >
                            {employeeDetails
                              ? `${employeeDetails?.firstName || ""} ${
                                  employeeDetails?.lastName || ""
                                }`
                              : "Employee not available"}
                          </td>
                          <td
                            style={{
                              position: isMobile ? "sticky" : "static",
                              left: isMobile ? "72px" : "auto",
                              zIndex: isMobile ? "8" : "auto",
                              minWidth: isMobile ? "80px" : "120px",
                              backgroundColor: isMobile ? "white" : "transparent",
                            }}
                            className="text-capitalize"
                          >
                            {clientDetails ? (
                              `${clientDetails?.firstName || ""} ${
                                clientDetails?.lastName || ""
                              }`
                            ) : (
                              "Client not available"
                            )}
                          </td>
                          <td
                            style={{
                              position: "static",
                              left: "auto",
                              zIndex: "auto",
                              minWidth: "100px",
                              backgroundColor: "transparent",
                            }}
                          >
                            {moment(event?.startTime).format("ll")}
                          </td>
                          <td>{moment(event?.startTime).format("hh:mm A")}</td>
                          <td>{moment(event?.endTime).format("hh:mm A")}</td>
                          <td>
                            {payoutDetails
                              ? moment(payoutDetails.checkIn, "HH:mm").format(
                                  "hh:mm A"
                                )
                              : "N/A"}
                          </td>
                          <td>
                            {payoutDetails
                              ? moment(payoutDetails.checkOut, "HH:mm").format(
                                  "hh:mm A"
                                )
                              : "N/A"}
                          </td>
                          <td className="sign-area">
                            {payoutDetails?.signature ? (
                              <img
                                src={payoutDetails?.signature}
                                className="digital-sign"
                                alt="Signature"
                              />
                            ) : (
                              "N/A"
                            )}
                          </td>
                          <td>
                            {hasPayout && payoutDetails.note?.trim()
                              ? payoutDetails.note
                              : "N/A"}
                          </td>
                          <td>
                            {hasPayout ? (
                              <img
                                src={images.edit}
                                alt="Edit"
                                className="cursor editImg"
                                onClick={() =>
                                  handleEditClick(payoutDetails, event.startTime)
                                }
                              />
                            ) : isGreaterThanToday ? <span title="Event should be in future">N/A</span> : (
                              <button
                                style={{
                                  backgroundColor: "#0d1326",
                                  color: "white",
                                  border: "none",
                                  padding: "5px 10px",
                                  borderRadius: "4px",
                                }}
                                type="button"
                                title="Timesheet"
                                onClick={() => handleTimesheet(event)}
                              >
                                Timesheet
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td style={{ textAlign: "center" }} colSpan={11}>
                        No Data Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={page}
              totalPages={data?.total_pages}
              setPage={setPage}
            />
          </div>
        </div>
      </div>
      {showEditModal && (
        <PayoutModal
          show={showEditModal}
          onClose={() => setShowEditModal(false)}
          payoutData={selectedPayout}
          onSave={(updatedPayout) => {
            updateMutation.mutate({
              id: selectedPayout?._id,
              body: updatedPayout,
            });
            setShowEditModal(false);
          }}
        />
      )}

      {filterShow && (
        <FilterModal
          show={filterShow}
          setShow={setFilterShow}
          refetch={refetch}
          data={filterData}
          setData={setFilterData}
        />
      )}
    </section>
  );
};

export default AdminPayout;